import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface SectionHeaderProps {
  title: string
  showNavigation?: boolean
  showViewAll?: boolean
  /** Hide View All button on mobile only (still visible on desktop) */
  hideViewAllOnMobile?: boolean
  viewAllHref?: string
  /** Custom text for View All button (defaults to "View All") */
  viewAllText?: string
  onPrevious?: () => void
  onNext?: () => void
  className?: string
  titleClassName?: string
}

export default function SectionHeader({
  title,
  showNavigation = false,
  showViewAll = false,
  hideViewAllOnMobile = false,
  viewAllHref = '#',
  viewAllText = 'View All',
  onPrevious,
  onNext,
  className = '',
  titleClassName = '',
}: SectionHeaderProps) {
  return (
    <div className={`flex items-center justify-between mb-4 md:mb-8 ${className}`}>
      <div className="flex items-center gap-2 md:gap-4">
        <h2 className={`text-[22px] md:text-[36px] font-black text-brand-navy ${titleClassName}`}>{title}</h2>
        {/* View All link — responsive: pill style on desktop, text style on mobile */}
        {showViewAll && (
          <LocalizedClientLink
            href={viewAllHref}
            className={`inline-flex text-xs md:text-sm font-medium transition-colors items-center gap-1 md:gap-2 outline-none focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:ring-offset-2 uppercase md:normal-case text-brand-primary-blue md:text-brand-navy md:border md:border-brand-navy md:rounded-lg md:px-4 md:py-2 md:hover:bg-gray-50 md:bg-white ${hideViewAllOnMobile ? 'hidden md:inline-flex' : ''}`}
          >
            {viewAllText}
            <ChevronRight className="w-4 h-4" aria-hidden="true" />
          </LocalizedClientLink>
        )}
      </div>
      {/* Navigation arrows - hidden on mobile (swipe is natural) */}
      {showNavigation && (
        <div className="hidden md:flex gap-2">
          <button
            onClick={onPrevious}
            className="p-2.5 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:ring-offset-2"
            aria-label="Previous items"
          >
            <ChevronLeft className="w-5 h-5 text-gray-500" aria-hidden="true" />
          </button>
          <button
            onClick={onNext}
            className="p-2.5 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:ring-offset-2"
            aria-label="Next items"
          >
            <ChevronRight className="w-5 h-5 text-gray-500" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  )
}
